import React from 'react';
import { ButtonGroup, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Eye, ShieldCheck, Bell } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';

const LOCKED_TOOLTIP = 'Cannot change until lock expires';

/**
 * CompactModeSelector - Small 3-button segmented control
 * Shows all options at once: Off | Track | Enforce (or Off | Track | Alert for alertOnly)
 *
 * @param {string[]} [disabledModes] - Mode values that cannot be selected (e.g. ['enforce'])
 * @param {Object.<string, string>} [disabledModeTooltips] - Per-mode tooltip when disabled
 */
const CompactModeSelector = ({
  value,
  onChange,
  disabled = false,
  alertOnly = false,
  locked = false,
  disabledModes = [],
  disabledModeTooltips = {},
  fullWidth = false,
}) => {
  const { colors } = useTheme();
  const success = colors.success || '#07CBA3';
  const warning = colors.warning || '#ED8936';
  
  const allModes = [
    { value: 'off', label: 'Off', icon: null, tooltip: 'Disabled' },
    { value: 'track', label: 'Track', icon: Eye, tooltip: 'Track violations and send push notifications when enabled' },
    { value: 'alert', label: 'Alert', icon: Bell, tooltip: 'Send email and push notifications when threshold is reached' },
    { value: 'enforce', label: 'Enforce', icon: ShieldCheck, tooltip: 'Block trades when limit is reached; also sends push when enabled' }
  ];
  
  // For alertOnly (max position loss), show: Off | Track | Alert
  // For regular rules, show: Off | Track | Enforce
  const modes = alertOnly 
    ? allModes.filter(m => m.value !== 'enforce')
    : allModes.filter(m => m.value !== 'alert');
  
  return (
    <ButtonGroup
      size="sm"
      data-testid="compact-mode-selector"
      style={{
        minWidth: fullWidth ? 0 : '160px',
        minHeight: 'unset',
        width: fullWidth ? '100%' : 'max-content',
        maxWidth: '100%',
      }}
      className="compact-mode-selector"
    >
      {modes.map(mode => {
        const Icon = mode.icon;
        const isActive = value === mode.value;
        const modeUnavailable = disabledModes.includes(mode.value);
        const cannotSelect = locked || disabled || modeUnavailable;
        const tooltipText = locked
          ? LOCKED_TOOLTIP
          : modeUnavailable && disabledModeTooltips[mode.value]
            ? disabledModeTooltips[mode.value]
            : mode.tooltip;
        const activeColor =
          mode.value === 'enforce'
            ? success
            : mode.value === 'alert'
              ? warning
              : mode.value === 'off'
                ? colors.textMuted
                : colors.primary;
        const activeBorder = isActive ? activeColor : colors.border;
        const activeBg = isActive ? activeColor : 'transparent';
        const activeText = isActive ? '#fff' : colors.text;

        return (
          <OverlayTrigger
            key={mode.value}
            placement="top"
            trigger={['hover', 'focus']}
            overlay={(props) => (
              <Tooltip id={`mode-${mode.value}`} {...props}>
                {tooltipText}
              </Tooltip>
            )}
          >
            <span style={{ display: 'flex', flex: 1 }}>
              <Button
                variant={isActive ? 'primary' : 'outline-secondary'}
                onMouseDown={(e) => {
                  if (cannotSelect) {
                    e.preventDefault();
                    return;
                  }
                  onChange(mode.value);
                }}
                onClick={(e) => {
                  if (cannotSelect) {
                    e.preventDefault();
                    return;
                  }
                  onChange(mode.value);
                }}
                // Keep enabled for tooltip hover; gate selection via cannotSelect
                aria-disabled={cannotSelect}
                style={{
                  flex: 1,
                  padding: '2px 6px',
                  fontSize: '0.7rem',
                  fontWeight: isActive ? 600 : 400,
                  borderColor: activeBorder,
                  backgroundColor: activeBg,
                  color: activeText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '3px',
                  minHeight: '26px',
                  lineHeight: 1.2,
                  opacity: modeUnavailable && !isActive ? 0.55 : (disabled ? 0.55 : 1),
                  cursor: cannotSelect ? 'not-allowed' : 'pointer',
                }}
              >
                {Icon && <Icon size={10} />}
                {mode.label}
              </Button>
            </span>
          </OverlayTrigger>
        );
      })}
    </ButtonGroup>
  );
};

export default CompactModeSelector;
